/**
 * Figma `217:8030`'s popover and `57:2`'s full page are one mechanism drawn
 * twice: campaigns within an organisation, organisations within the
 * membership listing (platform design §17.8). `SwitcherList` is that
 * mechanism; this file is its popover trigger, and `SwitcherPage.tsx` its
 * full-page rendering, sharing the one component rather than building two.
 *
 * Every row comes from `/control/me`'s `organisations` (`useMe`), never from
 * `memberships` -- the pooled, cross-organisation list a tenant-wide member's
 * own row cannot carry a campaign in. Reading `organisations[i].campaigns`
 * instead is what makes this member's own organisation's campaigns visible to
 * them.
 *
 * The Figma frames also draw a credit balance, a 7-day consumption number, a
 * campaign status chip, reach and account counts, an "em vigor desde" date
 * and a "Convites" rail card. `OrganisationEntry` carries the balance and
 * consumption (`owner-decisions-align-57-2-03`), `CampaignEntry` the status
 * chip and reach and member counts (`...-57-2-04`), and `GET /invitations`
 * already backs a caller's own invitations (`...-57-2-01`) -- but this
 * popover renders none of it -- `SwitcherPage.tsx`'s full-page listing does.
 * No field anywhere carries the "em vigor desde" date, so that alone stays
 * unrendered everywhere.
 */

import { useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Building2, ChevronsUpDown, Layers, Megaphone, Plus } from 'lucide-react';

import { IconHint } from '../../../chrome/hint';
import { Button } from '../../../ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../../ui/popover';
import { Skeleton } from '../../../ui/skeleton';
import { useSidebar } from '../../../ui/sidebar';
import { overviewPath } from '../../data/overview/paths';
import type { CampaignEntry } from '../session';
import { useMe } from '../session';
import { lastPageOr } from '../lastPage';
import { campaignHref, organisationHref, useOrgContext } from '../orgContext';
import { SWITCH_ALL_PATH } from './paths';
import { sw } from './messages';

const CREATE_CAMPAIGN_CAPABILITY = 'control_plane.write';

/**
 * Re-routes a campaign-scoped page to the same page under the new campaign
 * (the control plane's routes carry the campaign in the URL, and a caller
 * mid-page loses their place if this instead sent them to the campaign's own
 * home); a page whose route carries no `$campaignId` -- including every
 * other organisation's -- goes to the campaign's own destination instead,
 * since there is no "same page" to preserve across organisations.
 */
function useSwitchToCampaign(): (campaign: CampaignEntry) => void {
  const navigate = useNavigate();
  const routeParams = useParams({ strict: false }) as Record<string, string | undefined>;
  return (campaign) => {
    if (routeParams.campaignId !== undefined) {
      void navigate({ params: { ...routeParams, campaignId: campaign.campaign_id } as never });
    } else {
      void navigate({ to: campaignHref(campaign.campaign_id, lastPageOr(overviewPath)) });
    }
  };
}

/**
 * `AppShell` owns the failed state -- it gates on `useMe()` erroring before
 * rendering anything that reaches here -- but nothing owns the pending one:
 * the shell renders while `/control/me` is still in flight, so this renders
 * nothing until `me.data` genuinely exists.
 */
export function SwitcherList(props: { onNavigate?: () => void }): ReactNode {
  const me = useMe();
  const switchToCampaign = useSwitchToCampaign();
  const navigate = useNavigate();

  if (me.data === undefined) return null;
  const organisations = me.data.organisations;

  // A `role="listbox"` with no `role="option"`/`role="group"` descendant --
  // every organisation filtered out or none to begin with -- is an ARIA
  // violation cmdk's own `CommandEmpty` does not avoid: it renders plain
  // text inside the listbox, not a valid child role. Bypassing `Command`
  // entirely for a genuinely empty listing sidesteps it; a search that
  // matches nothing (`CommandEmpty` used for that alone) is not this case.
  if (organisations.length === 0) {
    return <p className="text-muted-foreground p-6 text-center text-sm">{sw('empty')}</p>;
  }

  return (
    <Command>
      <CommandInput placeholder={sw('search.placeholder')} />
      <CommandList>
        <CommandEmpty>{sw('search.empty')}</CommandEmpty>
        {organisations.map((organisation) => (
          <CommandGroup
            key={organisation.tenant_id}
            heading={<span className="sr-only">{organisation.tenant_name}</span>}
          >
            <CommandItem
              value={organisation.tenant_name}
              onSelect={() => {
                void navigate({ to: organisationHref(organisation.tenant_id) });
                props.onNavigate?.();
              }}
            >
              <Building2 aria-hidden />
              {organisation.tenant_name}
            </CommandItem>
            {organisation.campaigns.map((campaign) => (
              <CommandItem
                key={campaign.campaign_id}
                value={`${organisation.tenant_name} ${campaign.campaign_name}`}
                className="pl-8"
                onSelect={() => {
                  switchToCampaign(campaign);
                  props.onNavigate?.();
                }}
              >
                <Megaphone aria-hidden />
                {campaign.campaign_name}
              </CommandItem>
            ))}
            {organisation.campaigns.length === 0 &&
              organisation.capabilities.includes(CREATE_CAMPAIGN_CAPABILITY) && (
                <CommandItem
                  value={`${organisation.tenant_name} ${sw('createCampaign')}`}
                  className="pl-8"
                  onSelect={() => {
                    void navigate({ to: organisationHref(organisation.tenant_id) });
                    props.onNavigate?.();
                  }}
                >
                  <Plus aria-hidden />
                  {sw('createCampaign')}
                </CommandItem>
              )}
          </CommandGroup>
        ))}
      </CommandList>
    </Command>
  );
}

/**
 * The Shell's `switcher` slot. Holds its place with a placeholder while
 * `/control/me` is in flight, and renders nothing for a membership-less caller
 * (the home page explains that state in full) or a failed answer (the shell's
 * own state replaces the page).
 *
 * Figma `247:10768`/`122:2` (expanded) and `247:10807`/`122:176` (collapsed,
 * both `Kind`s): the tile always names the organisation, never the campaign,
 * because the collapsed rail draws one "Organização" tile regardless of
 * which `Kind` is current.
 */
export function OrganisationSwitcher(): ReactNode {
  const me = useMe();
  const context = useOrgContext();
  const [open, setOpen] = useState(false);
  const { state } = useSidebar();
  const collapsed = state === 'collapsed';

  if (me.isPending) {
    return (
      <Skeleton
        data-testid="organisation-switcher-loading"
        className={collapsed ? 'size-8' : 'h-9 w-full'}
      />
    );
  }
  if (me.data === undefined || me.data.organisations.length === 0) return null;

  const organisation =
    context.kind === 'organisation' || context.kind === 'campaign' ? context.organisation : null;
  const campaign = context.kind === 'campaign' ? context.campaign : null;
  const orgLabel = organisation?.tenant_name ?? sw('trigger.none');
  // A tenant-wide member's own organisation, real, with no campaign in it
  // yet -- distinct from "you belong to nothing" (the home page's own copy)
  // and told inline, not only inside the popover a caller has no reason to
  // open until they already suspect something is missing.
  const noCampaignYet = organisation !== null && organisation.campaigns.length === 0;

  const trigger = (
    <Button
      type="button"
      variant="outline"
      data-testid="organisation-switcher-trigger"
      className={
        collapsed
          ? 'size-8 shrink-0 p-0'
          : 'h-auto w-full justify-between gap-2 overflow-hidden py-2'
      }
    >
      <Building2 aria-hidden className="shrink-0" />
      {!collapsed && (
        <span className="flex min-w-0 flex-1 flex-col items-start overflow-hidden text-left">
          <span className="w-full truncate">{orgLabel}</span>
          {campaign !== null && (
            <span
              data-testid="organisation-switcher-campaign-line"
              className="text-muted-foreground w-full truncate text-xs font-normal"
            >
              {campaign.campaign_name}
            </span>
          )}
        </span>
      )}
      {!collapsed && <ChevronsUpDown aria-hidden className="shrink-0 opacity-50" />}
    </Button>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {collapsed ? (
        <IconHint label={orgLabel}>
          <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        </IconHint>
      ) : (
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      )}
      {noCampaignYet && !collapsed && (
        <p className="text-muted-foreground px-2 pt-1 text-sm">{sw('noCampaignYet')}</p>
      )}
      <PopoverContent align="start" aria-label={sw('dialog.label')} className="p-0">
        <SwitcherList onNavigate={() => setOpen(false)} />
        <div className="border-t p-1">
          <Button asChild type="button" variant="ghost" className="w-full justify-start gap-2">
            <Link to={SWITCH_ALL_PATH} onClick={() => setOpen(false)}>
              <Layers aria-hidden />
              {sw('viewAll')}
            </Link>
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
